import React, { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { equipmentUsageService } from '../services/dataService';
import EquipmentUsageView from '../components/EquipmentUsageView';
import dhaLogo from '../assets/img/dha-logo.png';
import publicStyles from './assets/css/PublicFacilityLookupPage.module.css';
import styles from './assets/css/DetailDashboard.module.css';
import usageStyles from '../components/EquipmentUsageView.module.css';

const CODE_KEY = 'equipment_usage_access_code';

const readStoredCode = () => {
  try {
    return sessionStorage.getItem(CODE_KEY) || '';
  } catch (err) {
    return '';
  }
};

const storeCode = (code) => {
  try {
    if (code) sessionStorage.setItem(CODE_KEY, code);
    else sessionStorage.removeItem(CODE_KEY);
  } catch (err) {
    // Storage is unavailable (private window); the code is kept for this page view only.
  }
};

/** Public equipment usage report; the access code is checked by the API on every request. */
const PublicEquipmentUsagePage = () => {
  const [accessCode, setAccessCode] = useState(readStoredCode);
  const [codeInput, setCodeInput] = useState('');
  const [codeError, setCodeError] = useState('');

  const fetchUsage = useCallback(
    (params) => equipmentUsageService.getPublic(accessCode, params),
    [accessCode],
  );

  const lock = useCallback((message = '') => {
    storeCode('');
    setAccessCode('');
    setCodeInput('');
    setCodeError(message);
  }, []);

  const handleError = useCallback((err) => {
    if (err?.response?.status === 403) lock('That access code is not valid.');
  }, [lock]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const code = codeInput.trim();
    if (!code) return;
    storeCode(code);
    setCodeError('');
    setAccessCode(code);
  };

  return (
    <div className={publicStyles.page}>
      <header className={publicStyles.header}>
        <div className={publicStyles.brand}>
          <img src={dhaLogo} alt="Digital Health Agency" />
          <div>
            <strong>Equipment Gateway</strong>
            <span>Equipment usage</span>
          </div>
        </div>
        <div className={usageStyles.headerActions}>
          {accessCode ? (
            <button type="button" className={usageStyles.headerButton} onClick={() => lock()}>Lock</button>
          ) : null}
          <Link to="/login" className={publicStyles.loginLink}>Staff login</Link>
        </div>
      </header>

      <main className={usageStyles.publicContent}>
        {accessCode ? (
          <div className={styles.page}>
            <div>
              <h1>Equipment Usage</h1>
              <p className={styles.muted}>
                Worklists published to each machine, how many it pulled and how many were completed, and which
                facilities are not pulling worklists, not sending results, or have misconfigured equipment.
              </p>
            </div>
            <EquipmentUsageView fetchUsage={fetchUsage} onError={handleError} />
          </div>
        ) : (
          <section className={`${styles.card} ${usageStyles.codeCard}`}>
            <h2>Equipment Usage</h2>
            <p className={styles.muted}>Enter the access code to view the report.</p>
            <form className={usageStyles.codeForm} onSubmit={handleSubmit}>
              <input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                className={styles.filterInput}
                value={codeInput}
                onChange={(event) => setCodeInput(event.target.value)}
                placeholder="Access code"
                aria-label="Access code"
                autoFocus
              />
              <button type="submit" className={styles.primaryButton} disabled={!codeInput.trim()}>View</button>
            </form>
            {codeError ? <div className={publicStyles.error}>{codeError}</div> : null}
          </section>
        )}
      </main>
    </div>
  );
};

export default PublicEquipmentUsagePage;
